import { clsx, type WithDataTestId } from "@regrello/core-utils";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router";

export interface RegrelloLinkV2Props extends RouterLinkProps, WithDataTestId {
  /** When `true`, will render the children without the link, and wrapped in a span. */
  disabled?: boolean;

  /**
   * When `true`, will render the RouterLink even when disabled. By default, only a span with a
   * `className` is rendered.
   *
   * @default false
   */
  forceRenderWhenDisabled?: boolean;

  /** Ref to the root element. */
  ref?: React.Ref<HTMLAnchorElement>;

  /** When `true`, will omit className assignment. */
  unstyled?: boolean;
}

/** A link that works with react-router instead of navigating with function calls. */
export function RegrelloLinkV2({
  children,
  dataTestId,
  disabled = false,
  forceRenderWhenDisabled = false,
  ref,
  unstyled = false,
  ...restProps
}: RegrelloLinkV2Props) {
  const className = unstyled
    ? ""
    : clsx("font-medium text-sm", {
        "text-primary-textMuted hover:underline": !disabled,
      });

  if (disabled && !forceRenderWhenDisabled) {
    return <span className={className}>{children}</span>;
  }

  return (
    <RouterLink ref={ref} data-testid={dataTestId} {...restProps} className={clsx(className, restProps.className)}>
      {children}
    </RouterLink>
  );
}
